import { Flex, MDIIcon, Modal } from '@solinkcorp/solink-components';
import Print from '@solinkcorp/solink-components/svg/print.svg?react';
import { type IEvent } from 'clients/PlatformApi';
import moment from 'moment';
import React, { useCallback } from 'react';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import { LocationStore } from 'stores/LocationStore';
import SystemReport from 'utils/SystemReport';
import TimeFormatter from 'utils/TimeFormatter';
import { DEFAULT_TIMEZONE } from 'vd-constants';
import EventDetails from '../EventsPage/EventDetails';
import './PrintPanel.sass';
import Thumbnails from './Thumbnails';
interface PrintPanelProps {
  printPanelOpen?: boolean;
  event: IEvent;
  closePanel?: () => void;
}

const PrintPanel: React.FC<PrintPanelProps> = ({ printPanelOpen, event, closePanel }) => {
  const locale = useLocale();

  const location = useFluxStore(
    LocationStore,
    (store) => event.siteId && store.getLocation(event.siteId),
  );

  const modalCloseAction = useCallback(() => {
    if (closePanel) {
      closePanel();
    }
  }, [closePanel]);

  const handlePrintClick = () => {
    const SR = new SystemReport().report;
    if (SR.browser.name === 'Safari') {
      document.execCommand('print', false, '');
    } else {
      window.print();
    }
  };

  const actions = [
    { text: locale.general.close, onClick: modalCloseAction, isPrimary: false },
    {
      text: locale.print.printButton,
      onClick: handlePrintClick,
      icon: <MDIIcon icon={Print} size={12} style={{ marginRight: '6px' }} />,
    },
  ];

  const { subtitle, title } = event;
  const description = event.details?.description;

  const timezone = location?.timezone || DEFAULT_TIMEZONE;
  const startAndEndAreSame =
    moment.tz(event.startTimeLocal, timezone).startOf('second').valueOf() ===
    moment.tz(event.endTimeLocal, timezone).startOf('second').valueOf();
  const startTimeFormat = TimeFormatter.getString({
    zeroPrefix: false,
    seconds: true,
    showTimezone: startAndEndAreSame,
  });
  const endTimeFormat = TimeFormatter.getString({
    zeroPrefix: false,
    seconds: true,
  });
  const dateFormat = locale.date.format1;
  const date = moment.tz(event.startTimeLocal, timezone).format(dateFormat);
  const startTime = moment.tz(event.startTimeLocal, timezone).format(startTimeFormat);
  const endTime = moment.tz(event.endTimeLocal, timezone).format(endTimeFormat);

  const timeDisplay = startAndEndAreSame ? startTime : `${startTime} - ${endTime}`;

  const subTitle = (
    <div>
      <div className="subtitle-content">{subtitle}</div>
      <Flex>
        <div className="subtitle-left-col">{date}</div>
        <div>{timeDisplay}</div>
      </Flex>
    </div>
  );

  return (
    <Modal
      className="print-modal"
      isOpen={printPanelOpen}
      onClose={modalCloseAction}
      title={title}
      subTitle={subTitle}
      actions={actions}
      modalWrapperClassName="print-modal-wrapper"
    >
      <div className="modal-content">
        {/* Event from virtual locations won't have the location object returned from the API. Virtual locations are data only locations that don't have cameras */}
        {location && <Thumbnails eventCameras={event.cameras} location={location} />}
        <div className="text-wrapper">
          {title && (
            <Flex justifyContent="center">
              <h3 className="title">{title}</h3>
            </Flex>
          )}
          {description && (
            <Flex justifyContent="center">
              <div className="description">{description}</div>
            </Flex>
          )}
          <Flex justifyContent="center">
            <pre className="details">
              <EventDetails event={event} timezone={location?.timezone} />
            </pre>
          </Flex>
        </div>
      </div>
    </Modal>
  );
};

export default PrintPanel;
